/**
 * Call-link revocation codec.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallLinkRevocation.swift`.
 *
 * Once an owner revokes a link, any receiver who has cached the link (or
 * learns of the revocation through the E2EE channel / a revocation index)
 * must refuse to redeem it. The revocation is an Ed25519 signature over
 * canonical bytes `roomId(16) ‖ revokedAt(LE8)` made under the same owner
 * key that signed the original link; the server cannot synthesise a
 * revocation without the owner's private key, and cannot rewrite the
 * roomId/revokedAt fields without invalidating the signature.
 *
 * Wire format:
 *   kaether:call:revoke:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "roomId": "<base64url-16bytes>",
 *     "revokedAt": 1700000100, "ownerSignature": "<base64-64-bytes>" }
 *
 * Note: ownerSignature in the JSON is standard base64 (not base64url) because
 * JSON serialisation of binary data uses standard base64 when marshalled as a
 * Uint8Array through JSON.stringify with custom replacer. We match iOS which
 * uses standard base64 encoding for the signature field inside the JSON.
 *
 * Pure logic / SubtleCrypto only. No SolidJS imports.
 */

import { CallLinkRoomId, base64UrlEncode, base64UrlDecode } from "./CallLink";

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type CallLinkRevocationErrorKind =
  | { tag: "notARevocation" }
  | { tag: "malformed"; reason: string }
  | { tag: "unsupportedSchema"; schema: number };

export class CallLinkRevocationError extends Error {
  readonly kind: CallLinkRevocationErrorKind;

  constructor(kind: CallLinkRevocationErrorKind) {
    super(CallLinkRevocationError.message(kind));
    this.name = "CallLinkRevocationError";
    this.kind = kind;
  }

  private static message(k: CallLinkRevocationErrorKind): string {
    switch (k.tag) {
      case "notARevocation":
        return "not a revocation";
      case "malformed":
        return `malformed revocation: ${k.reason}`;
      case "unsupportedSchema":
        return `unsupported revocation schema: ${k.schema}`;
    }
  }
}

// ---------------------------------------------------------------------------
// Value type
// ---------------------------------------------------------------------------

export interface CallLinkRevocationPayload {
  readonly schema: number; // = 1
  readonly roomId: string; // base64url, no padding
  readonly revokedAt: number; // Unix seconds (safe as JS number up to 2^53)
  readonly ownerSignature: Uint8Array; // 64-byte Ed25519 over canonical bytes
}

const CURRENT_SCHEMA = 1;

// ---------------------------------------------------------------------------
// Codec constants
// ---------------------------------------------------------------------------

export const CALL_LINK_REVOKE_PREFIX = "kaether:call:revoke:v1:" as const;

const ALG_ED25519 = { name: "Ed25519" } as const;

// ---------------------------------------------------------------------------
// Canonical bytes for the owner signature
// ---------------------------------------------------------------------------

/**
 * Canonical byte layout: 16-byte roomId ‖ 8-byte little-endian revokedAt.
 * Not the JSON — the signature is over the structural fields only. Mirrors
 * iOS `CallLinkRevocationCodec.canonicalBytes`.
 */
export function revocationCanonicalBytes(
  roomId: CallLinkRoomId,
  revokedAt: number,
): Uint8Array {
  const buf = new Uint8Array(16 + 8);
  buf.set(roomId.raw, 0);
  // revokedAt as 8-byte LE
  let v = BigInt(revokedAt);
  for (let i = 0; i < 8; i++) {
    buf[16 + i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** Cheap prefix check. Mirrors iOS `CallLinkRevocationCodec.isRevocation`. */
export function isCallLinkRevocation(s: string): boolean {
  return s.startsWith(CALL_LINK_REVOKE_PREFIX);
}

/**
 * Builds a signed revocation for a roomId. The signature binds
 * (roomId-bytes ‖ revokedAt-LE8) so the server cannot rebind a revocation
 * to a different room id without forging a new signature.
 *
 * `ownerPrivateKey` must be a CryptoKey with `["sign"]` usage (Ed25519).
 * Mirrors iOS `CallLinkRevocationCodec.encode`.
 */
export async function encodeCallLinkRevocation(
  roomId: CallLinkRoomId,
  revokedAt: number,
  ownerPrivateKey: CryptoKey,
): Promise<string> {
  const canonical = revocationCanonicalBytes(roomId, revokedAt);
  const sigBuf = await crypto.subtle.sign(ALG_ED25519, ownerPrivateKey, canonical);
  const sig = new Uint8Array(sigBuf);

  // Serialise to JSON. ownerSignature is encoded as standard base64 inside
  // JSON (mirrors how iOS JSONEncoder serialises Data — standard base64 with
  // padding).
  const json = JSON.stringify({
    schema: CURRENT_SCHEMA,
    roomId: roomId.asString(),
    revokedAt,
    ownerSignature: bytesToBase64(sig),
  });

  const jsonBytes = new TextEncoder().encode(json);
  return CALL_LINK_REVOKE_PREFIX + base64UrlEncode(jsonBytes);
}

/**
 * Parses a revocation string into its payload. Does NOT verify the
 * signature — that requires the owner's public key (see
 * `verifyCallLinkRevocation`). Throws `CallLinkRevocationError` on parse
 * failure. Mirrors iOS `CallLinkRevocationCodec.decode`.
 */
export function decodeCallLinkRevocation(s: string): CallLinkRevocationPayload {
  if (!s.startsWith(CALL_LINK_REVOKE_PREFIX)) {
    throw new CallLinkRevocationError({ tag: "notARevocation" });
  }
  const b64 = s.slice(CALL_LINK_REVOKE_PREFIX.length);
  const jsonBytes = base64UrlDecode(b64);
  if (jsonBytes === null) {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "invalid base64url",
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let parsed: any;
  try {
    parsed = JSON.parse(new TextDecoder().decode(jsonBytes));
  } catch (e) {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: `json decode failed: ${e instanceof Error ? e.message : String(e)}`,
    });
  }

  if (typeof parsed.schema !== "number") {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "missing schema field",
    });
  }
  if (parsed.schema !== CURRENT_SCHEMA) {
    throw new CallLinkRevocationError({
      tag: "unsupportedSchema",
      schema: parsed.schema as number,
    });
  }
  if (typeof parsed.roomId !== "string" || parsed.roomId === "") {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "empty or missing roomId",
    });
  }
  if (typeof parsed.revokedAt !== "number") {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "missing or non-numeric revokedAt",
    });
  }
  if (typeof parsed.ownerSignature !== "string") {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "missing ownerSignature",
    });
  }

  let sigBytes: Uint8Array;
  try {
    sigBytes = base64ToBytes(parsed.ownerSignature as string);
  } catch {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: "ownerSignature is not valid base64",
    });
  }

  if (sigBytes.length !== 64) {
    throw new CallLinkRevocationError({
      tag: "malformed",
      reason: `signature length ${sigBytes.length} !== 64`,
    });
  }

  return {
    schema: CURRENT_SCHEMA,
    roomId: parsed.roomId as string,
    revokedAt: parsed.revokedAt as number,
    ownerSignature: sigBytes,
  };
}

/**
 * Convenience: returns null for non-revocation strings. Mirrors iOS
 * `CallLinkRevocationCodec.tryDecode`.
 */
export function tryDecodeCallLinkRevocation(
  s: string,
): CallLinkRevocationPayload | null {
  if (!isCallLinkRevocation(s)) return null;
  try {
    return decodeCallLinkRevocation(s);
  } catch {
    return null;
  }
}

/**
 * Verifies the embedded signature against `ownerPublicKey`. Returns false on
 * any structural problem so callers can fail-closed. Mirrors iOS
 * `CallLinkRevocationCodec.verify`.
 *
 * `ownerPublicKey` is the raw 32-byte public key.
 */
export async function verifyCallLinkRevocation(
  payload: CallLinkRevocationPayload,
  ownerPublicKey: Uint8Array,
): Promise<boolean> {
  if (payload.ownerSignature.length !== 64) return false;

  // Decode roomId from base64url to reconstruct the CallLinkRoomId.
  const roomIdBytes = base64UrlDecode(payload.roomId);
  if (roomIdBytes === null || roomIdBytes.length !== 16) return false;

  let roomId: CallLinkRoomId;
  try {
    roomId = new CallLinkRoomId(roomIdBytes);
  } catch {
    return false;
  }

  let pub: CryptoKey;
  try {
    pub = await crypto.subtle.importKey(
      "raw",
      ownerPublicKey,
      ALG_ED25519,
      false,
      ["verify"],
    );
  } catch {
    return false;
  }

  const canonical = revocationCanonicalBytes(roomId, payload.revokedAt);
  try {
    return await crypto.subtle.verify(
      ALG_ED25519,
      pub,
      payload.ownerSignature,
      canonical,
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Internal base64 helpers (standard, not base64url — for JSON payload sig)
// ---------------------------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(s: string): Uint8Array {
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
